import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import 'moment-timezone';
import {
  numeroSegonsEntreDates,
  convertirSegonsAMinuts,
  convertirSegonsaHores,
  numeroSegonsEntreDates2,
  operacionsRestaHores,
  convertirMilisMinuts,
  convertirMilisHores
} from '../../Helpers/funcionsDates';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Font de dades (igual que DetallDiaScreen):
// Dia a la setmana actual → cp.detallSetmana
// Dia al calendari (altra setmana) → calendari.detallMes

export const ResumHoresDiaConsultat = ({ dataAvui }) => {
  const [contadorJornadaIniciada, setContadorJornadaIniciada] = useState(new Date());
  const { cp, calendari, configUsuari, ui } = useSelector(state => state);
  const { detallSetmana } = cp;
  const { contingut } = configUsuari;
  const [t] = useTranslation('generic');

  const diaKey = ui.diaConsultat?.dia ?? null;

  useEffect(() => {
    const interval = setInterval(() => setContadorJornadaIniciada(new Date()), 10000);
    return () => clearInterval(interval);
  }, []);

  const calculs = useMemo(() => {
    if (!diaKey || !contingut) return null;

    // ── Seleccionar font de dades ─────────────────────────────────────────
    const diaASetmana = detallSetmana?.fixatges_setmanals?.some(d => d.dia === diaKey);

    let detallDia = null;
    let incidenciesDia = [];

    if (diaASetmana) {
      // Font: setmana actual
      detallDia = detallSetmana.fixatges_setmanals.find(d => d.dia === diaKey);
      incidenciesDia = (detallSetmana.llistatIncidencies ?? []).filter(e => {
        const inici = e.DataInici_IncidenciaFix?.substring(0, 10);
        const fi    = e.DataFi_IncidenciaFix?.substring(0, 10);
        return diaKey >= inici && diaKey <= fi;
      });
    } else if (calendari.detallMes) {
      // Font: mes del calendari
      detallDia = calendari.detallMes.fixatgesMes?.find(d => d.dia === diaKey);
      incidenciesDia = (calendari.detallMes.incidenciesMes ?? []).filter(e => {
        const inici = e.DataInici_IncidenciaFix?.substring(0, 10);
        const fi    = e.DataFi_IncidenciaFix?.substring(0, 10);
        return diaKey >= inici && diaKey <= fi;
      });
    }

    if (!detallDia) return null;

    let segonsTotalJornada = 0;
    let segonsJustificats  = 0;
    let segonsNoJustificats = 0;
    let segonsNoAceptats   = 0;
    let segonsTeletreball  = 0;
    let segonsPresencial   = 0;
    let segonsBossaHores   = 0;

    // ── Incidències ────────────────────────────────────────────────────────
    incidenciesDia.forEach(e => {
      if (e.contahores_Incidencia != 1) return;
      const limitDiari   = (contingut.computHores / 5) * 60 * 60 * 1000;
      const durada       = numeroSegonsEntreDates(e.DataInici_IncidenciaFix, e.DataFi_IncidenciaFix);
      const duradaLimitada = durada > limitDiari ? limitDiari : durada;
      if (e.Justificat_IncidenciaFix == 1) {
        if ([1, 4, 12].includes(Number(e.IdTipusIncidencia_IncidenciaFix))) {
          segonsJustificats += durada;
        } else {
          segonsJustificats += duradaLimitada;
        }
      } else if (e.Justificat_IncidenciaFix == null) {
        segonsNoJustificats += duradaLimitada;
      } else {
        segonsNoAceptats += duradaLimitada;
      }
    });

    // ── Borsa d'hores ──────────────────────────────────────────────────────
    contingut?.llistatDiesBorsaHores?.diesBorsa.forEach(e => {
      if (e.dataBorsa === diaKey && e.estat === '1') {
        segonsBossaHores += e.minutsDemanats * 60 * 1000;
      }
    });

    // ── Fixatges ───────────────────────────────────────────────────────────
    const fixatges = detallDia.fixatges ?? [];
    const esAvui   = diaKey === moment().format('YYYY-MM-DD');

    for (let i = 0; i < fixatges.length; i += 2) {
      const entrada = fixatges[i];
      const sortida = fixatges[i + 1];
      if (!['1', '2'].includes(entrada.IdTipus_Fixatge)) continue;
      let segons;
      if (sortida) {
        segons = numeroSegonsEntreDates(entrada.DataHora_Fixatge, sortida.DataHora_Fixatge);
      } else if (esAvui) {
        segons = numeroSegonsEntreDates2(entrada.DataHora_Fixatge, contadorJornadaIniciada);
      } else {
        continue;
      }
      segonsTotalJornada += segons;
      if (entrada.IdTipus_Fixatge === '2') segonsTeletreball += segons;
      if (entrada.IdTipus_Fixatge === '1') segonsPresencial  += segons;
    }

    const segonsTotals  = segonsTotalJornada + segonsJustificats + segonsBossaHores;
    const computHores   = detallDia?.computHores ?? 0;
    let computSegons    = operacionsRestaHores(segonsTotals, computHores / 5);
    let deMes = false;
    let computSegonsDeMes = 0;
    if (computSegons < 0) {
      deMes = true;
      computSegonsDeMes = -computSegons;
      computSegons = 0;
    }

    return {
      horesTreballadesTotals: convertirSegonsaHores(segonsTotals),
      minutsTreballatsTotals: convertirSegonsAMinuts(segonsTotals),
      horesTeletreball:       convertirSegonsaHores(segonsTeletreball),
      minutsTeletreball:      convertirSegonsAMinuts(segonsTeletreball),
      horesPresencial:        convertirSegonsaHores(segonsPresencial),
      minutsPresencial:       convertirSegonsAMinuts(segonsPresencial),
      horesBossaHores:        convertirMilisHores(segonsBossaHores),
      minutsBossaHores:       convertirMilisMinuts(segonsBossaHores),
      horesJustificades:      convertirSegonsaHores(segonsJustificats),
      minutsJustificats:      convertirSegonsAMinuts(segonsJustificats),
      horesNoJustificades:    convertirSegonsaHores(segonsNoJustificats),
      minutsNoJustificats:    convertirSegonsAMinuts(segonsNoJustificats),
      horesNoAceptats:        convertirSegonsaHores(segonsNoAceptats),
      minutsNoAceptats:       convertirSegonsAMinuts(segonsNoAceptats),
      horesRestants:          parseInt(computSegons / (60 * 60)),
      minutsRestants:         parseInt((computSegons / 60) % 60),
      horesDesMes:            parseInt(computSegonsDeMes / (60 * 60)),
      minutsDeMes:            parseInt((computSegonsDeMes / 60) % 60),
      deMes,
    };
  }, [detallSetmana, calendari.detallMes, diaKey, contingut, contadorJornadaIniciada]);

  if (!calculs) return null;

  const {
    horesTeletreball, minutsTeletreball, horesPresencial, minutsPresencial,
    horesBossaHores, minutsBossaHores, horesJustificades, minutsJustificats,
    horesNoJustificades, minutsNoJustificats, horesNoAceptats, minutsNoAceptats,
    horesTreballadesTotals, minutsTreballatsTotals,
  } = calculs;

  return (
    <>
      {horesJustificades > 0 || minutsJustificats > 0 ? (
        <p><strong>{t('ResumHores.justificades')} </strong> {horesJustificades} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsJustificats} {t('Generics.minuts')}</p>
      ) : null}
      {horesNoJustificades > 0 || minutsNoJustificats > 0 ? (
        <p><strong>{t('ResumHores.noJustificades')}</strong> {horesNoJustificades} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsNoJustificats} {t('Generics.minuts')}</p>
      ) : null}
      {horesNoAceptats > 0 || minutsNoAceptats > 0 ? (
        <p><strong>{t('ResumHores.noAceptades')}</strong> {horesNoAceptats} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsNoAceptats} {t('Generics.minuts')}</p>
      ) : null}
      {horesTeletreball > 0 || minutsTeletreball > 0 ? (
        <p><strong>{t('ResumHores.teletreball')}</strong> {horesTeletreball} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsTeletreball} {t('Generics.minuts')}</p>
      ) : null}
      {horesPresencial > 0 || minutsPresencial > 0 ? (
        <p><strong>{t('ResumHores.presencial')} </strong> {horesPresencial} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsPresencial} {t('Generics.minuts')}</p>
      ) : null}
      {horesBossaHores > 0 || minutsBossaHores > 0 ? (
        <p><strong>{t('ResumHores.bossaHores')}</strong> {horesBossaHores} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsBossaHores} {t('Generics.minuts')}</p>
      ) : null}
      <p>
        <strong>{t('ResumHores.diaTotal')} </strong>
        {horesTreballadesTotals} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsTreballatsTotals} {t('Generics.minuts')}
      </p>
    </>
  );
};

export default React.memo(ResumHoresDiaConsultat);